/**
 * TanStack Query hooks for Cheque Settings
 */

import type {
  ChequeSettingsPublic,
  CreateChequeSettingsRequest,
  UpdateChequeSettingsRequest,
} from '@garden-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chequeSettingsApi } from '../services/organizationSettingsApi';

export const chequeSettingsQueryKeys = {
  all: ['chequeSettings'] as const,
  list: (includeInactive?: boolean) =>
    [...chequeSettingsQueryKeys.all, 'list', { includeInactive }] as const,
  detail: (id: string) => [...chequeSettingsQueryKeys.all, 'detail', id] as const,
  chequeNumber: (id: string) => [...chequeSettingsQueryKeys.all, 'chequeNumber', id] as const,
};

/**
 * Fetch all cheque settings
 */
export function useChequeSettings(includeInactive = false) {
  return useQuery({
    queryKey: chequeSettingsQueryKeys.list(includeInactive),
    queryFn: async () => {
      const response = await chequeSettingsApi.list(includeInactive);
      return response.data as ChequeSettingsPublic[];
    },
  });
}

/**
 * Fetch a single cheque settings
 */
export function useChequeSettingsById(id: string) {
  return useQuery({
    queryKey: chequeSettingsQueryKeys.detail(id),
    queryFn: async () => {
      const response = await chequeSettingsApi.get(id);
      return response.data as ChequeSettingsPublic;
    },
    enabled: !!id,
  });
}

/**
 * Create new cheque settings
 */
export function useCreateChequeSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateChequeSettingsRequest) => {
      const response = await chequeSettingsApi.create(data);
      return response.data as ChequeSettingsPublic;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: chequeSettingsQueryKeys.all,
      });
    },
  });
}

/**
 * Update cheque settings
 */
export function useUpdateChequeSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateChequeSettingsRequest }) => {
      const response = await chequeSettingsApi.update(id, data);
      return response.data as ChequeSettingsPublic;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: chequeSettingsQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: chequeSettingsQueryKeys.detail(variables.id),
      });
    },
  });
}

/**
 * Delete cheque settings
 */
export function useDeleteChequeSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await chequeSettingsApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: chequeSettingsQueryKeys.all,
      });
    },
  });
}

/**
 * Get current cheque number
 */
export function useCurrentChequeNumber(id: string) {
  return useQuery({
    queryKey: chequeSettingsQueryKeys.chequeNumber(id),
    queryFn: async () => {
      const response = await chequeSettingsApi.getCurrentChequeNumber(id);
      return response.data?.currentChequeNumber;
    },
    enabled: !!id,
  });
}

/**
 * Get next cheque number (increments the counter)
 */
export function useGetNextChequeNumber() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await chequeSettingsApi.getNextChequeNumber(id);
      return response.data?.nextChequeNumber;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: chequeSettingsQueryKeys.chequeNumber(id),
      });
      queryClient.invalidateQueries({
        queryKey: chequeSettingsQueryKeys.detail(id),
      });
    },
  });
}
